import React, { FC, useCallback, useEffect, useState } from 'react';
import { Button, WrongDataNotice, TextRecaptcha, SvgIcon, Icons } from '@homeproved/shared/ui';
import styled, { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CompanyForm, CompanyUserForm } from '@homeproved/shared/feature-company';
import {
  AcceptPolicyField,
  claimFormSchema,
  formatMobileNumber,
  FormGroup,
  FormSection,
  Input,
  processVatInput,
  refineCompanyUserForm,
  RegistrationFormData,
  registrationFormSchema,
  useRegistrationFormData,
} from '@homeproved/shared/feature-forms';
import { useLocalizedRoutes as useProLocalizedRoutes } from '@homeproved/pro/feature-localized-routes';
import { useLocalizedRoutes as useComLocalizedRoutes } from '@homeproved/com/feature-localized-routes';
import {
  CompaniesApiFactory,
  CompanyData,
  getErrorMessage,
  SectorDescendant,
  useApiFactory,
  useMutationFetch,
} from '@homeproved/shared/data-access';
import { useRouter } from 'next/router';
import { Box, useMediaQuery } from '@material-ui/core';
import { ActivityPicker, RequestCategoryModal } from '@homeproved/shared/feature-sectors';
import { prefillForm } from './step1/helpers';
import { ButtonWrapper, Wrapper } from './step1/Atoms';
import { useSnackbar } from 'notistack';
import ReactHtmlParser from 'react-html-parser';
import { useDisclosure } from 'react-use-disclosure';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Specializations } from '@homeproved/com/feature-company';

type RegistrationFormProps = {
  companyById?: CompanyData;
  isMobile: boolean;
};

const AlreadyClaimedWrapper = styled.div`
  margin: 4rem auto;
  max-width: 60rem;
  padding: 3rem 2rem;
  border-radius: ${({ theme }) => theme.config.defaultBorderRadius};
  background: #fff;
`;

const AlreadyBackdrop = styled.div`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: black;
  opacity: 0.2;
  z-index: 9;
`;

const AlreadyClaimed = styled.div`
  margin: 4rem auto;
  max-width: 60rem;
  padding: 3rem 2rem;
  border-radius: ${({ theme }) => theme.config.defaultBorderRadius};
  background: #fff;
  position: fixed;
  z-index: 10;
  top: 25vh;
  left: 0;
  right: 0;
`;

const CloseIcon = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  cursor: pointer;
`;

const StyledButton = styled(Button)`
  margin: 3rem auto 0;
  display: table;
`;

const InfoText = styled.div`
  font-family: ${({ theme }) => theme.config.fonts.PTSans};
  text-align: center;
  max-width: 55rem;
  margin: 0 auto;
`;

export const RegistrationStep1Form: FC<RegistrationFormProps> = ({ companyById, isMobile }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const activityPickerMobile = useMediaQuery(theme.breakpoints.down(700));
  const router = useRouter();
  const { getPath: proGetPath } = useProLocalizedRoutes();
  const { getPath: comGetPath } = useComLocalizedRoutes();
  const { setData: setRegistrationFormData } = useRegistrationFormData();
  const [focusHandled, setFocusHandled] = useState(false);
  const [selectedSectors, setSelectedSectors] = useState<number[]>([]);
  const methods = useForm<RegistrationFormData>({
    resolver: zodResolver(
      companyById
        ? refineCompanyUserForm(claimFormSchema)
        : refineCompanyUserForm(registrationFormSchema)
    ),
  });
  const policyAccepted = methods.watch('acceptPolicy', false);
  const [allowFormPrefill, setAllowFormPrefill] = useState(true);
  const [prefillRegistrationFormCompleted, setPrefillRegistrationFormCompleted] = useState(false);
  const { data: prefilledRegistrationFormData } = useRegistrationFormData();
  const [existingCompany, setExistingCompany] = useState<CompanyData>();
  const [showAlreadyClaimed, setShowAlreadyClaimed] = useState(false);
  const [companyByIdHandled, setCompanyByIdHandled] = useState(false);

  const companiesApi = useApiFactory(CompaniesApiFactory);
  const { mutation } = useMutationFetch('registerCompany', companiesApi.apiAuthRegisterCompanyPost);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const {
    isOpen: isRequestCategoryModalOpen,
    close: onCloseRequestCategoryModal,
    open: onOpenRequestCategoryModal,
  } = useDisclosure(false);

  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleSubmit = async (data: RegistrationFormData) => {
    const result = data;

    executeRecaptcha('registration').then(() => {
      result.vat = processVatInput(data.vat);
      result.userMobile = formatMobileNumber(result.userMobile);
      setAllowFormPrefill(false);
      setRegistrationFormData({
        ...result,
        mobile: formatMobileNumber(result.mobile),
        sectorIds: selectedSectors,
      });

      mutation.mutate({
        ...result,
        vat: processVatInput(data.vat),
        latitude: parseFloat(data.latitude),
        longitude: parseFloat(data.longitude),
        mobile: formatMobileNumber(result.mobile),
        sectorIds: selectedSectors,
        companyId: companyById ? companyById.id : null,
        acceptPolicy: policyAccepted,
      });
    });
  };

  const translateErrorMessage = useCallback(
    (message) => {
      switch (message[0]) {
        case 'User not unique':
          return t('app.pro.pages.registration.steps.step1.errors.userNotUnique');
        case 'Company already exists':
          return t('app.pro.pages.registration.steps.step1.errors.companyAlreadyExists');
        case 'Company is already in a claiming procedure':
          return t('app.pro.pages.registration.steps.step1.errors.companyInClaimingProcedure');
        case 'The user already has another company':
          return t('app.pro.pages.registration.steps.step1.errors.userHasAnotherCompany');
        default:
          return message;
      }
    },
    [t]
  );

  useEffect(() => {
    if (mutation.isSuccess) {
      closeSnackbar();
      router
        .push(
          `${proGetPath('/registration/step2')}?email=${encodeURIComponent(
            mutation.data.data.relations?.user?.data?.email
          )}&firstname=${encodeURIComponent(
            mutation.data.data.relations?.user?.data?.firstName
          )}&lastname=${encodeURIComponent(mutation.data.data.relations?.user?.data?.lastName)}`,
          undefined,
          { shallow: true }
        )
        .then();
    }
    if (mutation.isError) {
      enqueueSnackbar(translateErrorMessage(getErrorMessage(mutation, t)), {
        variant: 'error',
      });
      mutation.reset();
    }
  }, [
    router,
    proGetPath,
    mutation,
    mutation.isSuccess,
    t,
    closeSnackbar,
    enqueueSnackbar,
    translateErrorMessage,
  ]);

  const handleFocusAfterFetchByVat = (company?: CompanyData) => {
    setExistingCompany(company);
    if (!focusHandled) {
      const elementId = company ? 'userFirstName' : 'email';
      if (elementId != null) {
        document.getElementById(elementId)?.focus();
      }
    }
    setFocusHandled(true);
  };

  // Extract setValue method from methods, to be able to pass it as a separate dependency to useEffect
  const setValue = methods.setValue;

  const handleCloseClaimedPopup = (e) => {
    e.stopPropagation();
    setValue('vat', null);
    setShowAlreadyClaimed(false);
  };

  useEffect(() => {
    if (existingCompany && executeRecaptcha) {
      if (!existingCompany.claimedAt && !!companyById) {
        const setValueConfig = { shouldValidate: false, shouldDirty: true };
        setValue('vat', existingCompany.vat, setValueConfig);
        setValue('name', existingCompany.name, setValueConfig);
        setValue('email', existingCompany.email, setValueConfig);
        setValue('phone', existingCompany.phone, setValueConfig);
        setValue('mobile', formatMobileNumber(existingCompany.mobile), setValueConfig);
        setValue('url', existingCompany.url, setValueConfig);

        const sectors = [];
        existingCompany.relations.sectors.forEach((sector) =>
          sector.data.descendants.forEach((subSector: SectorDescendant) =>
            sectors.push(subSector.data.id)
          )
        );
        setSelectedSectors(sectors);
      }

      if (existingCompany.claimedAt != null) {
        setShowAlreadyClaimed(true);
      }
    }
  }, [existingCompany, setValue, companyById, executeRecaptcha]);

  useEffect(() => {
    if (!!companyById && !companyByIdHandled) {
      setExistingCompany(companyById);
      setCompanyByIdHandled(true);
    }
  }, [companyById, companyByIdHandled]);

  useEffect(() => {
    if (prefilledRegistrationFormData && allowFormPrefill) {
      prefillForm(prefilledRegistrationFormData, methods, setSelectedSectors);
      setPrefillRegistrationFormCompleted(true);
      setAllowFormPrefill(false);
    }
  }, [allowFormPrefill, methods, prefilledRegistrationFormData]);
  return executeRecaptcha ? (
    <Wrapper>
      <form onSubmit={methods.handleSubmit(handleSubmit)}>
        <FormProvider {...methods}>
          {(!companyById || companyById.vat == null || companyById.email == null) && (
            <FormSection
              title={
                companyById
                  ? t('app.pro.pages.registration.steps.step1.titleCompany')
                  : t('app.pro.pages.registration.steps.step1.titleAddress')
              }
            >
              <CompanyForm
                onUpdateByCompany={handleFocusAfterFetchByVat}
                onTavNumberChanged={() => setExistingCompany(undefined)}
                companyById={companyById}
                prefillRegistrationFormCompleted={prefillRegistrationFormCompleted}
              />
              {companyById &&
                !isMobile &&
                (companyById.vat == null || companyById.email == null) && <WrongDataNotice />}
            </FormSection>
          )}
          {!!companyById && companyById.vat != null && (
            <Input name="vat" type="hidden" ref={methods.register} />
          )}
          {!!companyById && companyById.email != null && (
            <Input name="email" type="hidden" ref={methods.register} />
          )}
          <FormSection title={t('app.pro.pages.registration.steps.step1.titleUser')}>
            <CompanyUserForm isMobile={isMobile} />
          </FormSection>
          {!companyById && (
            <FormSection title={t('app.com.pages.addCompany.activitiesAndSpecializations')}>
              {companyById ? (
                <Specializations
                  specializations={companyById.relations.sectors}
                  mobile={isMobile}
                  omitTitle
                />
              ) : (
                <ActivityPicker
                  value={selectedSectors}
                  onChange={setSelectedSectors}
                  isMobile={activityPickerMobile}
                  openRequestCategoryModal={onOpenRequestCategoryModal}
                />
              )}
            </FormSection>
          )}
          <FormGroup>
            <AcceptPolicyField control={methods.control} comGetPath={comGetPath} />
          </FormGroup>
          {showAlreadyClaimed && (
            <AlreadyClaimedWrapper>
              <AlreadyClaimed>
                <CloseIcon onClick={handleCloseClaimedPopup}>
                  <SvgIcon icon={Icons.CROSS} size={1.2} color={'dark'} />
                </CloseIcon>
                <InfoText>{ReactHtmlParser(t('app.com.pages.addCompany.claimedCompany'))}</InfoText>
                <StyledButton
                  target={'_blank'}
                  href={
                    process.env.NEXT_PUBLIC_COM_URL +
                    comGetPath('/company/:slug/reviews', { slug: existingCompany.slug })
                  }
                >
                  {t('app.com.pages.addCompany.viewProfile')}
                </StyledButton>
              </AlreadyClaimed>
              <AlreadyBackdrop />
            </AlreadyClaimedWrapper>
          )}
          <ButtonWrapper>
            <Button
              type="submit"
              size="large"
              disabled={
                (!existingCompany && selectedSectors.length === 0) ||
                !policyAccepted ||
                mutation.isLoading ||
                (existingCompany && existingCompany.claimedAt != null)
              }
              isLoading={mutation.isLoading}
            >
              {t('shared.form.buttons.next')}
            </Button>
            <Box pt={1}>
              <TextRecaptcha center />
            </Box>
          </ButtonWrapper>
        </FormProvider>
      </form>
      {companyById && (isMobile || (companyById.vat !== null && companyById.email != null)) && (
        <WrongDataNotice />
      )}
      <RequestCategoryModal
        open={isRequestCategoryModalOpen}
        onClose={onCloseRequestCategoryModal}
      />
    </Wrapper>
  ) : null;
};
